import { Formik } from 'formik';
import { useState } from 'react';

import { Messages } from 'config';
import { ButtonColors } from 'types';
import { useAppSelector } from 'store';
import { Flex } from 'components/flex';
import { Icon } from 'components/icon';
import { InputField } from 'components/fields';
import * as CommonStyles from 'components/styles';
import { selectAuthUser } from 'store/slices/auth';
import { Dialog, DialogType } from 'components/dialog';
import { useAlert, isMobile, handleMutation } from 'hooks';
import { Button, ButtonTypes, ButtonWithRef } from 'components/button';
import {
  useChangeEmailMutation,
  useConfirmChangeEmailMutation,
} from 'store/api/users';

import { commonButtonProps } from '../config';

import { FormValues } from './types';
import { ResendButton } from './resend-button';
import { initialValues, validationSchema } from './config';

export const ChangeEmailDialog = () => {
  const mobile = isMobile();
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);
  const { showSuccessAlert } = useAlert();
  const authUser = useAppSelector(selectAuthUser);
  const id = Number(authUser?.id);

  const [changeEmail, changeEmailMutation] = useChangeEmailMutation();
  const [confirmChangeEmail, confirmChangeEmailMutation] =
    useConfirmChangeEmailMutation();

  handleMutation(changeEmailMutation);
  handleMutation(confirmChangeEmailMutation);

  const isLastStep = step === 1;

  return (
    <Dialog
      open={open}
      showCloseButton
      title="Change email"
      closeOnDocumentClick={false}
      onClose={() => {
        setStep(0);
        setOpen(false);
      }}
      trigger={
        <ButtonWithRef
          {...commonButtonProps}
          tooltip="Change email"
          onClick={() => setOpen(true)}
        />
      }
    >
      {(close, _, ButtonWrap) => (
        <Formik<FormValues>
          initialValues={initialValues}
          validationSchema={validationSchema[step]}
          onSubmit={async (values, { setSubmitting }) => {
            if (isLastStep) {
              const confirmResponse = await confirmChangeEmail({
                ...values,
                id,
              }).unwrap();

              if (confirmResponse.message) {
                showSuccessAlert(`Email ${Messages.UPDATED_SUCCESSFULLY}`);
                close();
              }
            } else {
              const response = await changeEmail({
                id,
                newEmail: values.newEmail,
              }).unwrap();

              if (response.message) {
                setSubmitting(false);
                setStep((prevStep) => prevStep + 1);
              }
            }
          }}
        >
          {({ dirty, values, isSubmitting }) => (
            <CommonStyles.Form gap={24}>
              {step === 0 && (
                <InputField
                  name="newEmail"
                  label="New Email"
                  placeholder="Enter new email"
                />
              )}

              {isLastStep && (
                <Flex gap={24} flexDirection="column">
                  <Flex justifyContent="center">
                    <Icon.EmailLetter />
                  </Flex>

                  <p>
                    We sent a verification code to the email attached to your
                    account. The code will be valid for 15 minutes.
                  </p>

                  <Flex column gap={16}>
                    <InputField
                      number
                      name="code"
                      label="Code"
                      placeholder="Enter code"
                    />

                    <div>
                      <ResendButton
                        onResend={async () => {
                          const response = await changeEmail({
                            id,
                            newEmail: values.newEmail,
                          }).unwrap();
                          if (response.message) {
                            showSuccessAlert(
                              `Verification code resent successfully`
                            );
                          }
                        }}
                      />
                    </div>
                  </Flex>
                </Flex>
              )}

              <ButtonWrap type={DialogType.Plain}>
                <Button
                  fullWidth={mobile}
                  colorType={ButtonColors.LightBlue}
                  text={isLastStep ? 'Go back' : 'Cancel'}
                  onClick={
                    isLastStep
                      ? () => setStep((prevStep) => prevStep - 1)
                      : close
                  }
                />
                <Button
                  fullWidth={mobile}
                  type={ButtonTypes.Submit}
                  disabled={!dirty || isSubmitting}
                  text={isLastStep ? 'Confirm' : 'Get code'}
                />
              </ButtonWrap>
            </CommonStyles.Form>
          )}
        </Formik>
      )}
    </Dialog>
  );
};
